import { createEntityAdapter, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { useCallback } from 'react';
import { getFullName } from '@shared/helpers';
import { useAppDispatch, useAppSelector } from '@shared/hooks';
import type { Client } from '@shared/types/users';
import { AppModule } from '@src/api/common.ts';
import type { ClientState } from './lib';

const clientsListAdapter = createEntityAdapter({
  selectId: (client: Client) => client.id,
});

const clientsInitialState = {
  isSelectionOn: false,
  selectedClients: [] as number[],
  countOfClients: 0,
};

const clientsListSlice = createSlice({
  name: 'clients/clientList',
  initialState: { ...clientsListAdapter.getInitialState(), ...clientsInitialState },
  reducers: {
    clientsAdd: clientsListAdapter.addMany,
    clientsRemove: clientsListAdapter.removeAll,
    clientRemove: clientsListAdapter.removeOne,
    clientAdd: clientsListAdapter.addOne,
    clientsSetAll: clientsListAdapter.setAll,
    clientSet: clientsListAdapter.setOne,
    setSelection: (state, action: PayloadAction<boolean>) => {
      state.selectedClients = [];
      state.isSelectionOn = action.payload;
    },
    select: (state, action: PayloadAction<{ id: number; isSelectedNow: boolean }>) => {
      const { id, isSelectedNow } = action.payload;
      if (isSelectedNow) {
        !state.selectedClients.includes(id) && state.selectedClients.push(id);
      } else {
        state.selectedClients = state.selectedClients.filter((clientId) => clientId !== id);
      }
    },
    selectAll: (state) => {
      state.selectedClients = [...state.ids] as number[];
    },
    setCountOfClients: (state, action: PayloadAction<number>) => {
      state.countOfClients = action.payload;
    },
  },
});

export const clientsListReducer = clientsListSlice.reducer;
export const clientsListActions = clientsListSlice.actions;

const clientsSelectors = clientsListAdapter.getSelectors();

const initialState: ClientState = {
  selectedTab: 'profile',
  selectedModule: null,
  isShowingActiveTabOnMobile: false,
  client: null,
};

export interface ChangeTabPayload {
  tab: string;
  module?: AppModule;
}

export const client = createSlice({
  name: 'clients/client',
  initialState,
  reducers: {
    select: (state, action) => {
      state.client = action.payload;
    },
    changeTab: (state, action: PayloadAction<ChangeTabPayload>) => {
      const { tab, module } = action.payload;
      state.selectedTab = tab;
      state.selectedModule = module ?? null;
      state.isShowingActiveTabOnMobile = true;
    },
    closeTabOnMobile: (state) => {
      state.isShowingActiveTabOnMobile = false;
    },
  },
});

const selectedClientSelector = (state: RootState) => state.clients.client.client;
const selectedClientsSelector = (state: RootState) => state.clients.list.selectedClients;

export const useClients = () => {
  const dispatch = useAppDispatch();
  const clients = useAppSelector((state) => clientsSelectors.selectAll(state.clients.list));
  const clientIds = useAppSelector((state) => clientsSelectors.selectIds(state.clients.list));
  const countOfClients = useAppSelector((state) => state.clients.list.countOfClients);

  const selectedClients = useAppSelector(selectedClientsSelector);
  const selectedUserIdsSelector = () => {
    const userIds: number[] = [];
    clients.map((client: Client) => {
      if (selectedClients.includes(client.id)) userIds.push(client.user.id);
    });

    return userIds;
  };
  const isClientSelected = (id: number) => selectedClients.includes(id);
  const isSelectionOn = useAppSelector((state) => state.clients.list.isSelectionOn);
  const isSelectedAll = selectedClients.length === clients.length;
  const selectedCount = selectedClients.length;

  const selectedClient = useAppSelector(selectedClientSelector);

  const selectedClientData = clients.find((cl) => cl.id === selectedClient);

  const setCountOfClients = (count: number) =>
    dispatch(clientsListActions.setCountOfClients(count));

  const select = (id?: number) => dispatch(client.actions.select(id));
  const selectForList = (id: number, isSelectedNow: boolean) =>
    dispatch(clientsListSlice.actions.select({ id, isSelectedNow }));
  const selectAll = () => dispatch(clientsListSlice.actions.selectAll());

  const addClients = (clients: Client[]) => dispatch(clientsListActions.clientsAdd(clients));
  const removeClients = () => dispatch(clientsListActions.clientsRemove());
  const removeClient = (id: number) => dispatch(clientsListActions.clientRemove(id));

  const getClientById = useCallback(
    (id?: number | null) => (id ? clients.find((client) => client.id === id) : undefined),
    [clients]
  );

  const addClient = (client: Client) => dispatch(clientsListActions.clientAdd(client));

  const setClients = (clients: Client[]) => dispatch(clientsListActions.clientsSetAll(clients));

  const setClient = (client: Client) => dispatch(clientsListActions.clientSet(client));

  const nameClients: string[] | undefined = clients.map((client) => getFullName(client));

  return {
    clients,
    selectedUserIdsSelector,
    addClients,
    isSelectionOn,
    isClientSelected,
    isSelectedAll,
    selectedClient,
    selectedClientData,
    selectedCount,
    select,
    selectForList,
    selectAll,
    getClientById,
    removeClients,
    removeClient,
    clientIds,
    addClient,
    setClients,
    setClient,
    nameClients,
    countOfClients,
    setCountOfClients,
  };
};

export const clientActions = client.actions;

export const useClient = (id: number) => {
  const client = useAppSelector((state) => clientsSelectors.selectById(state.clients.list, id));

  return {
    client,
  };
};
